import { createSelector } from 'redux-orm';

import orm from '../orm';
import { selectPath } from './router';
import {
  selectCurrentUserId,
  selectHasPermission,
  selectAvailableResourcesForCurrentUser,
} from './users';
import { selectConfig } from './root';
import { isLocalId } from '../utils/local-id';

export const makeSelectProjectById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Project }, id) => {
      const projectModel = Project.withId(id);

      if (!projectModel) {
        return projectModel;
      }

      return projectModel.ref;
    },
  );

export const selectProjectById = makeSelectProjectById();

export const selectCurrentProject = createSelector(
  orm,
  (state) => selectPath(state).projectId,
  ({ Project }, id) => {
    if (!id) {
      return id;
    }

    const projectModel = Project.withId(id);

    if (!projectModel) {
      return projectModel;
    }

    return projectModel.ref;
  },
);

export const selectRoleAssignmentsForCurrentProject = createSelector(
  orm,
  (state) => selectPath(state).projectId,
  ({ UserRoleAssignment }, projectId) => {
    if (!projectId) {
      return [];
    }

    return UserRoleAssignment.filter({
      resourceType: 'Project',
      resourceId: Number(projectId),
    })
      .toModelArray()
      .map((assignmentModel) => ({
        ...assignmentModel.ref,
        user: assignmentModel.user?.ref,
        role: assignmentModel.role?.ref,
      }));
  },
);

export const selectPrivateBoardsForCurrentUser = createSelector(
  orm,
  (state) => selectPath(state).projectId,
  (state) => selectCurrentUserId(state),
  (state) => selectConfig(state).isOrgMode,
  selectAvailableResourcesForCurrentUser,
  ({ Project }, projectId, currentUserId, isOrgMode, { boardIds }) => {
    const projectModels = isOrgMode
      ? Project.all().toModelArray()
      : [Project.withId(projectId)].filter(Boolean);

    if (!isOrgMode && !projectId) {
      return [];
    }

    return projectModels.flatMap((projectModel) =>
      projectModel
        .getOrderedBoardsQuerySet()
        .toModelArray()
        .filter((boardModel) => boardIds.has(boardModel.id) && !boardModel.isPublic)
        .map((boardModel) => ({
          ...boardModel.ref,
          isPersisted: !isLocalId(boardModel.id),
        })),
    );
  },
);

export const selectSharedBoardsForCurrentUser = createSelector(
  orm,
  (state) => selectPath(state).projectId,
  (state) => selectCurrentUserId(state),
  (state) => selectConfig(state).isOrgMode,
  selectAvailableResourcesForCurrentUser,
  ({ Project }, projectId, currentUserId, isOrgMode, { boardIds }) => {
    const projectModels = isOrgMode
      ? Project.all().toModelArray()
      : [Project.withId(projectId)].filter(Boolean);

    if (!isOrgMode && !projectId) {
      return [];
    }

    return projectModels.flatMap((projectModel) =>
      projectModel
        .getOrderedBoardsQuerySet()
        .toModelArray()
        .filter((boardModel) => boardIds.has(boardModel.id) && boardModel.isPublic)
        .map((boardModel) => ({
          ...boardModel.ref,
          isPersisted: !isLocalId(boardModel.id),
        })),
    );
  },
);

// export const selectEditableBoardsForCurrentUser = createSelector(
//   orm,
//   (state) => state,
//   ({ Board }, state) => {
//     return Board.all()
//       .toModelArray()
//       .filter((boardModel) => {
//         const canEdit = selectHasPermission(state, 'Board', boardModel.id, 'board_edit');

//         console.log('[RBAC] Board editable check', {
//           boardId: boardModel.id,
//           canEdit,
//         });

//         return canEdit;
//       })
//       .map((boardModel) => ({
//         ...boardModel.ref,
//         isPersisted: !isLocalId(boardModel.id),
//         lists: boardModel
//           .getOrderedListsQuerySet()
//           .toRefArray()
//           .map((list) => ({
//             ...list,
//             isPersisted: !isLocalId(list.id),
//           })),
//       }));
//   },
// );

export const selectCanManageCurrentProject = createSelector(
  orm,
  (state) => state,
  (state) => selectPath(state).projectId,
  (_, state, projectId) => {
    if (!projectId) {
      return false;
    }

    const canManage = selectHasPermission(state, 'Project', Number(projectId), 'project_manage');

    console.log('[RBAC] selectCanManageCurrentProject', {
      projectId,
      canManage,
    });

    return canManage;
  },
);

export default {
  selectProjectById,
  selectCurrentProject,
  selectRoleAssignmentsForCurrentProject,
  selectCanManageCurrentProject,
  selectPrivateBoardsForCurrentUser,
  selectSharedBoardsForCurrentUser,
  // selectEditableBoardsForCurrentUser,
};
